import { Contact, ContactInput, ContactsResponse, Category, CategoryInput, CategoriesResponse, ImportResult, BulkCategoryUpdateResponse } from '../types';
import { CrmContactsResponse, CrmDetailResponse, CrmOverviewResponse, CrmTerritorialOverviewResponse, CrmTerritorialProfileResponse } from '../types/crm';

const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
export const API_BASE_URL = rawBaseUrl.endsWith('/') ? rawBaseUrl.slice(0, -1) : rawBaseUrl;

export const buildApiUrl = (endpoint: string): string => {
  if (!endpoint) return API_BASE_URL;
  const hasProtocol = /^https?:\/\//i.test(endpoint);
  if (hasProtocol) return endpoint;
  const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${normalized}`;
};

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = buildApiUrl(endpoint);
    const token = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(buildApiUrl(url), {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      const statusText = typeof error.status === 'string' ? error.status.toUpperCase() : '';
      const isPaymentRequired =
        response.status === 402 ||
        error.paymentRequired ||
        error.requiresPayment ||
        statusText === 'PAYMENT_REQUIRED' ||
        statusText === 'REQUIRES_PAYMENT' ||
        statusText === 'PAST_DUE' ||
        statusText === 'EXPIRED';

      if (isPaymentRequired) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('app:payment-required', { detail: error }));
        }
        const paymentError = new Error(error.error || error.message || 'Pagamento necessário');
        (paymentError as any).paymentRequired = true;
        (paymentError as any).paymentNotice = error;
        throw paymentError;
      }

      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`${API_BASE_URL}/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`${API_BASE_URL}/contatos`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`${API_BASE_URL}/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`${API_BASE_URL}/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  async updateContactsCategory(contactIds: string[], categoriaId: string | null): Promise<BulkCategoryUpdateResponse> {
    return this.request<BulkCategoryUpdateResponse>('/contatos/categoria/em-lote', {
      method: 'POST',
      body: JSON.stringify({
        contactIds,
        categoriaId: categoriaId || null,
      }),
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>(`${API_BASE_URL}/categorias/all`);
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`${API_BASE_URL}/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>(`${API_BASE_URL}/categorias`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`${API_BASE_URL}/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`${API_BASE_URL}/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  async getCrmOverview(): Promise<{ success: boolean; data: CrmOverviewResponse }> {
    return this.request<{ success: boolean; data: CrmOverviewResponse }>('/crm/overview');
  }

  async getDashboardSummary() {
    return this.request<{
      date: string;
      sendActions: {
        total: number;
        byStatus?: Record<string, number>;
        metrics?: {
          totalRecipients?: number;
          sent?: number;
          delivered?: number;
          read?: number;
          failed?: number;
        };
      };
      conversations: {
        total: number;
        unread: number;
        active: number;
      };
      messages: {
        total: number;
        today: number;
        inbound: number;
        outbound: number;
        byType?: Record<string, number>;
      };
      sessions: {
        total: number;
        active: number;
      };
    }>('/dashboard/summary');
  }

  async getCrmContacts(params?: Record<string, string | number | undefined>): Promise<{ success: boolean } & CrmContactsResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });

    const endpoint = `/crm/contacts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return this.request<{ success: boolean } & CrmContactsResponse>(endpoint);
  }

  async getCrmContact(id: string): Promise<{ success: boolean } & CrmDetailResponse> {
    return this.request<{ success: boolean } & CrmDetailResponse>(`/crm/contacts/${id}`);
  }

  async createCrmContact(data: Record<string, unknown>) {
    return this.request(`/crm/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCrmContact(id: string, data: Record<string, unknown>) {
    return this.request(`/crm/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateCrmStage(id: string, statusJornada: string) {
    return this.request(`/crm/contacts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ statusJornada }),
    });
  }

  async createCrmInteraction(id: string, data: Record<string, unknown>) {
    return this.request(`/crm/contacts/${id}/interactions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createCrmTask(id: string, data: Record<string, unknown>) {
    return this.request(`/crm/contacts/${id}/tasks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCrmTask(id: string, data: Record<string, unknown>) {
    return this.request(`/crm/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getCrmTags() {
    return this.request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/crm/tags`);
  }

  async createCrmTag(data: Record<string, unknown>) {
    return this.request(`/crm/tags`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignCrmTag(contactId: string, tagId: string) {
    return this.request(`/crm/contacts/${contactId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tagId }),
    });
  }

  async removeCrmTag(contactId: string, tagId: string) {
    return this.request(`/crm/contacts/${contactId}/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  async getCrmCampaigns() {
    return this.request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/crm/campaigns`);
  }

  async getTerritorialOverview(params?: {
    periodDays?: number;
    campaignId?: string;
    channel?: string;
  }): Promise<{ success: boolean; data: CrmTerritorialOverviewResponse }> {
    const searchParams = new URLSearchParams();

    if (params?.periodDays) searchParams.set('periodDays', String(params.periodDays));
    if (params?.campaignId) searchParams.set('campaignId', params.campaignId);
    if (params?.channel) searchParams.set('channel', params.channel);

    const endpoint = `/crm/territorial/overview${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return this.request<{ success: boolean; data: CrmTerritorialOverviewResponse }>(endpoint);
  }

  async getTerritorialProfile(contactId: string): Promise<{ success: boolean; data: CrmTerritorialProfileResponse }> {
    return this.request<{ success: boolean; data: CrmTerritorialProfileResponse }>(`/crm/territorial/profile/${contactId}`);
  }

  async linkCrmCampaign(contactId: string, campaignId: string) {
    return this.request(`/crm/contacts/${contactId}/campaigns`, {
      method: 'POST',
      body: JSON.stringify({ campaignId }),
    });
  }
}

export const apiService = new ApiService();
